/**
 * COL-220: Admin reassign-tenant-room mutations + lookups.
 *
 * The atomic commit goes through the reassign_bookings_atomically Postgres
 * function (added in 20260524130000_reassign_tenant_room_rpc.sql) so the
 * chain of moves either fully lands or fully rolls back. Two-step UPDATEs
 * over PostgREST can't give us that guarantee.
 *
 * Lookup hooks are scoped to "active" bookings — the same predicate the
 * V2 admin header uses (status in confirmed/active/approved, not archived).
 * That's the set of tenants whose displacement we have to resolve.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";
import type { QueryPollOptions } from "@/lib/query-poll";

/** Booking statuses that count as "actively occupying" a room. */
export const ACTIVE_BOOKING_STATUSES = [
  "confirmed",
  "active",
  "approved",
] as const;

export type ReassignMove = {
  booking_id: number;
  new_room_id: number;
};

/**
 * Lightweight shape returned by the active-booking-per-room lookup.
 * Used to display who would be displaced if the admin picks this room.
 */
export type ActiveOccupant = {
  bookingId: number;
  userId: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  roomId: number;
};

/**
 * Look up the single active booking for a given room (if any).
 *
 * Returns null when the room is vacant. If somehow >1 active booking
 * exists (data drift), returns the most recently updated — the dialog
 * surfaces the violation either way at commit time.
 */
export function useActiveBookingForRoom(
  roomId: number | null | undefined,
  /** When set, exclude this booking — useful so the dialog doesn't
   *  treat the tenant being reassigned as "displacing" themselves. */
  excludeBookingId?: number | null
) {
  return useQuery({
    queryKey: [
      "v2",
      "reassign",
      "active-booking-for-room",
      roomId,
      excludeBookingId ?? null,
    ],
    enabled: !!roomId,
    queryFn: async (): Promise<ActiveOccupant | null> => {
      if (!roomId) return null;
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, user_id, name, email, phone, room_id, status, admin_archived_at, updated_at"
        )
        .eq("room_id", roomId)
        .is("admin_archived_at", null)
        .in("status", ACTIVE_BOOKING_STATUSES as unknown as string[])
        .order("updated_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as Array<{
        id: number;
        user_id: string | null;
        name: string | null;
        email: string | null;
        phone: string | null;
        room_id: number;
      }>;
      const filtered = excludeBookingId
        ? rows.filter((r) => r.id !== excludeBookingId)
        : rows;
      if (!filtered.length) return null;
      const top = filtered[0];
      return {
        bookingId: top.id,
        userId: top.user_id,
        name: top.name,
        email: top.email,
        phone: top.phone,
        roomId: top.room_id,
      };
    },
  });
}

/**
 * Fetch active occupants for an entire property's room list in one go.
 *
 * Returned as a map keyed by room_id so the dialog can mark "occupied"
 * vs "vacant" without firing N queries.
 */
export function usePropertyActiveOccupants(
  propertyId: number | null | undefined
) {
  return useQuery({
    queryKey: ["v2", "reassign", "property-occupants", propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<Map<number, ActiveOccupant>> => {
      if (!propertyId) return new Map();
      // Pull all active bookings on rooms in this property. We rely on
      // the rooms→property FK and filter via the embedded join.
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, user_id, name, email, phone, room_id, status, admin_archived_at, rooms!inner(property_id)"
        )
        .eq("rooms.property_id", propertyId)
        .is("admin_archived_at", null)
        .in("status", ACTIVE_BOOKING_STATUSES as unknown as string[]);
      if (error) throw error;
      const rows = (data ?? []) as Array<{
        id: number;
        user_id: string | null;
        name: string | null;
        email: string | null;
        phone: string | null;
        room_id: number;
      }>;
      const map = new Map<number, ActiveOccupant>();
      for (const r of rows) {
        // If data drift produces two actives, the first one wins — the
        // RPC will surface the violation at commit anyway.
        if (!map.has(r.room_id)) {
          map.set(r.room_id, {
            bookingId: r.id,
            userId: r.user_id,
            name: r.name,
            email: r.email,
            phone: r.phone,
            roomId: r.room_id,
          });
        }
      }
      return map;
    },
  });
}

/**
 * Fetch every room across every property, in one go.
 *
 * Used by ReassignRoomDialog when cross-property reassignment is in
 * scope (COL-220 expanded to room-level). Returned ordered by property
 * then sort_order then title so admins see a stable grouping.
 *
 * Cheap: rooms is a small table (<1k rows even at portfolio scale) and
 * we select only the columns the dialog actually renders.
 */
export function useAllRooms(enabled: boolean = true, poll?: QueryPollOptions) {
  return useQuery({
    queryKey: ["v2", "reassign", "all-rooms"],
    enabled,
    ...poll,
    // rooms has no archived_at on prod (dev only), and selecting a missing
    // column fails the whole request — keep the select to columns both have.
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rooms")
        .select("id, property_id, title, price, is_hidden, status")
        .order("property_id", { ascending: true })
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("title", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Active occupants across every property — same shape as
 * usePropertyActiveOccupants but with no property filter. Keyed by
 * room_id so the dialog can mark "occupied" vs "vacant" in O(1).
 */
export function useAllActiveOccupants(
  enabled: boolean = true,
  poll?: QueryPollOptions
) {
  return useQuery({
    queryKey: ["v2", "reassign", "all-occupants"],
    enabled,
    ...poll,
    queryFn: async (): Promise<Map<number, ActiveOccupant>> => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, user_id, name, email, phone, room_id, status, admin_archived_at"
        )
        .is("admin_archived_at", null)
        .in("status", ACTIVE_BOOKING_STATUSES as unknown as string[]);
      if (error) throw error;
      const rows = (data ?? []) as Array<{
        id: number;
        user_id: string | null;
        name: string | null;
        email: string | null;
        phone: string | null;
        room_id: number;
      }>;
      const map = new Map<number, ActiveOccupant>();
      for (const r of rows) {
        // Data drift: first active booking wins. The RPC re-checks the
        // invariant at commit, so a stale duplicate won't slip through.
        if (!map.has(r.room_id)) {
          map.set(r.room_id, {
            bookingId: r.id,
            userId: r.user_id,
            name: r.name,
            email: r.email,
            phone: r.phone,
            roomId: r.room_id,
          });
        }
      }
      return map;
    },
  });
}

/**
 * Commit a chain of room reassignments atomically.
 *
 * The RPC also re-derives each moved booking's base rent/deposit from its new
 * room and re-prices its unpaid rent + deposit rows (COL-360: the room is the
 * source of truth, the booking only carries a copy). So on success we
 * invalidate everything that could have stale room→tenant OR money data:
 * per-room booking lists, per-booking details, property room lists, occupant
 * maps, and every payments query.
 */
export function useReassignBookings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (moves: ReassignMove[]): Promise<void> => {
      if (!moves.length) return;
      const { error } = await supabase.rpc("reassign_bookings_atomically", {
        moves,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      // V2 admin surfaces (TenantDetail, PropertyDetail → Rooms tab).
      qc.invalidateQueries({ queryKey: ["v2", "bookings"] });
      qc.invalidateQueries({ queryKey: ["v2", "rooms"] });
      qc.invalidateQueries({ queryKey: ["v2", "reassign"] });
      // V1 admin tenants list (/admin?tab=tenants) loads bookings under
      // a separate query key — without invalidating it, the row still
      // shows the old room/property until the user reloads.
      invalidateOccupancyViews(qc);
      invalidateBookingMoneyQueries(qc);
    },
  });
}

/**
 * The move (or a re-sync) re-priced the tenant's open rent/deposit rows
 * server-side — every ledger view (tenant profile payments tab, admin
 * payments, reminders, journey deposit state) must refetch or it shows the
 * old room's amounts next to the new room's name.
 */
function invalidateBookingMoneyQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["v2", "payments"] });
  qc.invalidateQueries({ queryKey: ["v2", "journey"] });
  qc.invalidateQueries({ queryKey: ["v2", "admin-tenant-payments"] });
  qc.invalidateQueries({ queryKey: ["admin-payments"] });
  qc.invalidateQueries({ queryKey: ["payments"] });
  qc.invalidateQueries({ queryKey: ["tenant-payments"] });
}

/** What admin_resync_booking_to_room reports back. */
export type ResyncBookingResult = {
  synced: boolean;
  reason?: string;
  monthly_rent_before: number | null;
  monthly_rent_after: number | null;
  deposit_amount_before: number | null;
  deposit_amount_after: number | null;
  deposit_locked: boolean | null;
  rent_rows_repriced: number;
  deposit_row: "none" | "repriced" | "unchanged" | "canceled";
};

/**
 * Re-derive one booking's rent/deposit and unpaid ledger from its room.
 *
 * The repair the drift notice offers: the room is the source of truth, the
 * booking only carries a copy, and this puts the copy back in step without a
 * reassign (the tenant is already in the right room) or a SQL console.
 * Same admin gate and arithmetic as the reassign; one audit row per call.
 */
export function useResyncBookingToRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bookingId: number): Promise<ResyncBookingResult> => {
      const { data, error } = await supabase.rpc(
        "admin_resync_booking_to_room",
        {
          p_booking_id: bookingId,
        }
      );
      if (error) throw error;
      return data as ResyncBookingResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "bookings"] });
      invalidateOccupancyViews(qc);
      invalidateBookingMoneyQueries(qc);
    },
  });
}
